import { Monitor } from 'lucide-react';

import { Panel, PanelHeader } from '@/components/ui/Panel';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { EmptyState, InlineError } from '@/components/ui/EmptyState';
import { TableSkeleton } from '@/components/ui/Skeleton';
import { useToast } from '@/components/ui/Toast';
import { useSessions } from '@/lib/queries';
import { useRevokeSession } from '@/lib/mutations';
import { toMessage } from '@/lib/api';
import { absoluteDateTime, relativeTime } from '@/lib/format';

export function SessionsSection() {
  const toast = useToast();
  const sessions = useSessions();
  const revoke = useRevokeSession();

  const signOutSession = async (sessionId: string): Promise<void> => {
    try {
      await revoke.mutateAsync(sessionId);
      toast.success('Сессия завершена');
    } catch (error) {
      toast.error('Не удалось завершить сессию', toMessage(error));
    }
  };

  return (
    <Panel padding="none">
      <PanelHeader
        title="Активные сессии"
        description="Все браузеры, где сейчас выполнен вход в ваш аккаунт. Завершите те, которые не узнаёте, и смените пароль."
      />

      {sessions.isPending ? (
        <TableSkeleton rows={3} columns={4} />
      ) : sessions.isError ? (
        <div className="p-4">
          <InlineError
            message={toMessage(sessions.error, 'Не удалось загрузить список сессий.')}
            onRetry={() => void sessions.refetch()}
          />
        </div>
      ) : sessions.data.length === 0 ? (
        <EmptyState
          icon={Monitor}
          title="Других сессий нет"
          description="Это единственный браузер, где выполнен вход в ваш аккаунт."
        />
      ) : (
        <ul className="divide-y divide-line">
          {sessions.data.map((session) => (
            <li
              key={session.id}
              className="flex flex-wrap items-start justify-between gap-3 px-4 py-3"
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="truncate text-base font-medium text-fg">
                    {session.location ?? 'Местоположение неизвестно'}
                  </p>
                  {session.isCurrent ? <Badge tone="accent">Это устройство</Badge> : null}
                </div>
                <p className="mt-0.5 truncate text-sm text-fg-secondary" title={session.userAgent}>
                  {session.userAgent}
                </p>
                <p className="num mt-0.5 text-xs text-fg-muted">
                  {session.ipAddress} · вход {absoluteDateTime(session.createdAt)} · активность{' '}
                  {relativeTime(session.lastSeenAt)}
                </p>
              </div>

              {!session.isCurrent ? (
                <Button
                  size="sm"
                  variant="danger"
                  disabled={revoke.isPending}
                  onClick={() => void signOutSession(session.id)}
                >
                  Завершить
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}
